// src/components/layout/TopBar.tsx

import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Bell, ChevronDown, LogOut, User, Settings, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/types";
import type { UserRole } from "@/types";
import { clsx } from "clsx";

// ── Breadcrumb config ────────────────────────────────────────
const ROUTE_LABELS: Record<string, string> = {
  "/":            "Dashboard",
  "/audiences":   "Audience Builder",
  "/campaigns":   "Campaigns",
  "/intelligence":"Intelligence Console",
  "/reporting":   "Reporting Cockpit",
  "/partners":    "Partner Portal",
};

// ── Role switcher options (dev tool) ─────────────────────────
const ROLE_OPTIONS: { role: UserRole; label: string; org: string }[] = [
  { role: "mrn_admin",        label: "MRN Admin",        org: "MRN" },
  { role: "retailer_admin",   label: "Retailer Admin",   org: "Midwest Hardware" },
  { role: "retailer_analyst", label: "Retailer Analyst", org: "Midwest Hardware" },
  { role: "brand_manager",    label: "Brand Manager",    org: "DeWalt Tools" },
  { role: "brand_analyst",    label: "Brand Analyst",    org: "DeWalt Tools" },
  { role: "agency_user",      label: "Agency User",      org: "Omnicom Media" },
];

interface TopBarProps {
  sidebarCollapsed: boolean;
}

export default function TopBar({ sidebarCollapsed }: TopBarProps) {
  const { user, login } = useAuth();
  const location = useLocation();
  const [userMenuOpen, setUserMenuOpen]   = useState(false);
  const [roleMenuOpen, setRoleMenuOpen]   = useState(false);
  const [notifOpen,    setNotifOpen]      = useState(false);

  const pageLabel = ROUTE_LABELS[location.pathname] ?? "MRN Platform";

  return (
    <header
      className={clsx(
        "fixed top-0 right-0 h-14 z-30 flex items-center justify-between px-6",
        "bg-white border-b border-gray-200 transition-all duration-200",
        sidebarCollapsed ? "left-16" : "left-60"
      )}
    >
      {/* Left — breadcrumbs */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-slate text-xs">MRN</span>
        <span className="text-gray-300">/</span>
        <span className="font-medium text-navy">{pageLabel}</span>
      </div>

      {/* Right — actions */}
      <div className="flex items-center gap-2">

        {/* Role switcher (dev tool) */}
        <div className="relative">
          <button
            onClick={() => { setRoleMenuOpen(o => !o); setUserMenuOpen(false); setNotifOpen(false); }}
            className={clsx(
              "flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium",
              "bg-navy/8 text-navy hover:bg-navy/12 transition-colors duration-150"
            )}
            title="Switch role (dev tool)"
          >
            <RefreshCw size={11} />
            <span>{user ? ROLE_LABELS[user.role] : "No role"}</span>
            <ChevronDown size={11} />
          </button>

          {roleMenuOpen && (
            <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50 animate-slide-in">
              <p className="px-3 py-1.5 text-2xs font-semibold text-slate uppercase tracking-wider">
                Switch Role
              </p>
              {ROLE_OPTIONS.map(opt => (
                <button
                  key={opt.role}
                  onClick={() => { login(opt.role); setRoleMenuOpen(false); }}
                  className={clsx(
                    "w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors",
                    user?.role === opt.role ? "text-blue font-medium" : "text-gray-700"
                  )}
                >
                  <div className="font-medium">{opt.label}</div>
                  <div className="text-xs text-slate">{opt.org}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => { setNotifOpen(o => !o); setUserMenuOpen(false); setRoleMenuOpen(false); }}
            className="relative w-8 h-8 flex items-center justify-center rounded-md text-slate hover:bg-gray-100 hover:text-navy transition-colors duration-150"
          >
            <Bell size={16} />
            <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-cyan" />
          </button>

          {notifOpen && (
            <div className="absolute right-0 top-full mt-1 w-72 bg-white rounded-lg shadow-lg border border-gray-100 z-50 animate-slide-in">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="font-semibold text-navy text-sm">Notifications</p>
              </div>
              <div className="divide-y divide-gray-50">
                {[
                  { title: "Lapse risk alert", body: "142 FreshMart members at risk", time: "2m ago", color: "bg-red-500" },
                  { title: "Intent surge detected", body: "Power tools +340% this week", time: "18m ago", color: "bg-cyan" },
                  { title: "Campaign launched", body: "DeWalt Q2 Display is now live", time: "1h ago",  color: "bg-blue" },
                  { title: "Report ready", body: "April Attribution Report generated", time: "3h ago", color: "bg-emerald-500" },
                ].map((n, i) => (
                  <div key={i} className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer">
                    <span className={clsx("w-2 h-2 rounded-full mt-1.5 flex-shrink-0", n.color)} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800">{n.title}</p>
                      <p className="text-xs text-slate truncate">{n.body}</p>
                    </div>
                    <span className="text-2xs text-slate flex-shrink-0">{n.time}</span>
                  </div>
                ))}
              </div>
              <div className="px-4 py-2.5 border-t border-gray-100">
                <button className="text-xs text-blue hover:text-navy font-medium transition-colors">
                  View all notifications
                </button>
              </div>
            </div>
          )}
        </div>

        {/* User menu */}
        <div className="relative">
          <button
            onClick={() => { setUserMenuOpen(o => !o); setNotifOpen(false); setRoleMenuOpen(false); }}
            className="flex items-center gap-2 pl-2 pr-1 py-1 rounded-md hover:bg-gray-100 transition-colors duration-150"
          >
            <div className="w-7 h-7 rounded-full bg-navy flex items-center justify-center">
              <span className="text-white text-xs font-semibold">{user?.avatarInitials}</span>
            </div>
            <span className="text-sm font-medium text-gray-700 hidden sm:block">{user?.name}</span>
            <ChevronDown size={14} className="text-slate" />
          </button>

          {userMenuOpen && (
            <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50 animate-slide-in">
              <div className="px-3 py-2 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-800">{user?.name}</p>
                <p className="text-xs text-slate">{user?.email}</p>
              </div>
              <button className="w-full text-left flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                <User size={14} className="text-slate" />
                Profile
              </button>
              <button className="w-full text-left flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                <Settings size={14} className="text-slate" />
                Settings
              </button>
              <div className="border-t border-gray-100 mt-1 pt-1">
                <button className="w-full text-left flex items-center gap-2.5 px-3 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors">
                  <LogOut size={14} />
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Click-outside overlay */}
      {(userMenuOpen || roleMenuOpen || notifOpen) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => { setUserMenuOpen(false); setRoleMenuOpen(false); setNotifOpen(false); }}
        />
      )}
    </header>
  );
}